/**
 * When someone starts and stops talking, from meter readings alone (CHANGE_voice_level3 §3). Pure: fed a level
 * (0..1, the recorder's meter scale) and a time in ms, it says when speech has begun and when the utterance is over,
 * so it can be tested with made-up sequences instead of a microphone.
 */
export interface VadOptions {
  /** The quietest a frame can be and still count as speech, however quiet the room. */
  level: number;
  /** Sustained speech before an utterance has begun (and, for barge-in, before the agent stops). */
  startMs: number;
  /** Silence after speech that ends the utterance. */
  endSilenceMs: number;
  /** The longest an utterance may run. */
  maxMs: number;
}

export const VAD_DEFAULTS: VadOptions = { level: 0.1, startMs: 300, endSilenceMs: 1200, maxMs: 90_000 };
export const BARGE_IN_MS = 400;

export type VadEvent = "speech" | "end" | "cap";

export class Segmenter {
  private readonly o: VadOptions;
  private began: number | null = null;
  private last: number | null = null;
  private voiced = 0;        // ms of recent speech; decays in quiet frames, so a gap between syllables is not a stop
  private silence = 0;
  private floor: number | null = null;
  started = false;
  private over = false;

  constructor(options: Partial<VadOptions> = {}) {
    this.o = { ...VAD_DEFAULTS, ...options };
  }

  /** Speech is louder than both the fixed minimum and the room: an engine or a fan raises the bar with it. */
  threshold(): number {
    return Math.max(this.o.level, (this.floor ?? 0) * 2.5);
  }

  /** One meter reading. Says "speech" once when an utterance begins, and "end" or "cap" once when it is over. */
  push(level: number, t: number): VadEvent | null {
    if (this.over) return null;
    const dt = this.last === null ? 0 : Math.max(0, t - this.last);
    this.last = t;
    this.began ??= t;
    const loud = level >= this.threshold();
    if (!loud) this.floor = this.floor === null ? level : this.floor * 0.98 + level * 0.02;
    if (loud) {
      this.voiced += dt;
      this.silence = 0;
      if (!this.started && this.voiced >= this.o.startMs) {
        this.started = true;
        return "speech";
      }
    } else {
      this.voiced = Math.max(0, this.voiced - dt);
      if (this.started) {
        this.silence += dt;
        if (this.silence >= this.o.endSilenceMs) { this.over = true; return "end"; }
      }
    }
    if (this.started && t - this.began >= this.o.maxMs) { this.over = true; return "cap"; }
    return null;
  }
}
